import { useState, useEffect } from 'react';
import AuthShell from './ui/AuthShell';
import { buildAppUrl } from '../lib/siteUrl';
import { useNavigate, useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabaseClient';
import { resolveAuthSession } from '../lib/auth/storedSession';
import { submitAuth } from '../lib/auth/submitAuth';
import { readPendingAction } from '../lib/pendingAction';
import { isSafeRelativeUrl } from '../utils/safeRedirect';
import { clearAllQueryCache } from '../lib/query/clearQueryCache';
import { useToast } from '../contexts/ToastContext';
import {
  toEffectiveAuthSession,
  clearPasswordRecoveryActive,
  formatOtpVerificationError,
  markPasswordRecoveryActive,
} from '../utils/authRecovery';
import Button from './ui/Button';
import TextInput from './ui/TextInput';
import PasswordInput from './ui/PasswordInput';
import PasswordRules from './ui/PasswordRules';
import {
  EMAIL_RESEND_SECONDS,
  friendlyAuthError,
  isPasswordAcceptable,
  isValidEmail,
  normalizeEmail,
  suggestEmailFix,
} from '../lib/authValidation';

/** The reset code's length. Supabase makes it a project setting (6-10); this project sends 8. */
const OTP_LENGTH = 8;

const isUuid = (v) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v);

// The reset email's link must be a real web address. Inside the iPhone app window.location.origin is
// capacitor://localhost, which Supabase will not redirect to and Safari cannot open.
function getResetPasswordRedirectUrl() {
  return buildAppUrl('/reset-password');
}

/**
 * A double-tap on an email address selects all of it. Browsers break words at "." and "@", so the native double-tap
 * picks one fragment ("samburtch9") and there is no way to grab the whole address to fix or copy it (operator,
 * 2026-09-28). Tracked by pointer-up timing, since iOS Safari does not reliably fire dblclick; the select() waits a
 * beat so it lands after the browser's own word selection.
 */
let lastEmailTap = 0;
function selectWholeOnDoubleTap(e) {
  const input = e.currentTarget;
  const now = Date.now();
  if (now - lastEmailTap < 350 && input.value) {
    lastEmailTap = 0;
    setTimeout(() => input.select(), 40);
    return;
  }
  lastEmailTap = now;
}

/** Email field plus its one-line hint: "doesn't look right" once you leave it, or a tap-to-fix for a typo'd domain. */
export function EmailField({
  value,
  onChange,
  onBlur,
  showError,
  disabled,
  name,
  autoComplete = 'email',
}) {
  const fix = suggestEmailFix(value);
  const invalid = showError && Boolean(value.trim()) && !isValidEmail(value);
  return (
    <>
      <TextInput
        type="email"
        name={name}
        placeholder="Email"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        autoComplete={autoComplete}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        inputMode="email"
        onPointerUp={selectWholeOnDoubleTap}
        invalid={invalid}
        aria-invalid={invalid || undefined}
        disabled={disabled}
      />
      {fix ? (
        <p className="auth-field-hint">
          Did you mean{' '}
          <button type="button" className="auth-field-hint__fix" onClick={() => onChange(fix)}>
            {fix}
          </button>
          ?
        </p>
      ) : invalid ? (
        <p className="auth-field-hint auth-field-hint--error">
          Enter a full email, like name@example.com.
        </p>
      ) : null}
    </>
  );
}

export default function Auth() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [session, setSession] = useState(null);
  const [isSignUp, setIsSignUp] = useState(false);
  const [forgotStep, setForgotStep] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);
  const [sendCodeLoading, setSendCodeLoading] = useState(false);
  const [resetSubmitLoading, setResetSubmitLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [hasPendingInvite, setHasPendingInvite] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  // An error is about what was typed; once you change it, the error goes.
  useEffect(() => {
    setMessage((m) => (m.type === 'error' ? { type: '', text: '' } : m));
  }, [email, password, fullName, resetEmail, resetCode, resetPassword, resetConfirmPassword]);

  // "Resend code" counts down: Supabase refuses a second email to the same address within a minute.
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = window.setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => window.clearTimeout(t);
  }, [resendIn]);
  const queryParams = new URLSearchParams(location.search);
  const nextParam = queryParams.get('next');
  const safeNextPath = nextParam && isSafeRelativeUrl(nextParam) ? nextParam : null;
  const signupFromQuery = queryParams.get('signup') === '1';

  const redirectAfterAuth = () => {
    if (safeNextPath) {
      window.location.href = safeNextPath;
      return true;
    }

    const pendingInvite = sessionStorage.getItem('pendingInviteUrl');
    if (pendingInvite && isSafeRelativeUrl(pendingInvite)) {
      sessionStorage.removeItem('pendingInviteUrl');
      window.location.href = pendingInvite;
      return true;
    } else if (pendingInvite) {
      sessionStorage.removeItem('pendingInviteUrl');
    }
    const pendingJoin = readPendingAction();
    if (
      pendingJoin?.type === 'join_group_chat' &&
      pendingJoin.groupId &&
      isUuid(String(pendingJoin.groupId))
    ) {
      const invite = pendingJoin.inviteCode
        ? `?invite=${encodeURIComponent(pendingJoin.inviteCode)}`
        : '';
      window.location.href = `/groups/${pendingJoin.groupId}${invite}`;
      return true;
    }
    return false;
  };

  const clearForgotState = () => {
    setForgotStep(null);
    setResetEmail('');
    setResetCode('');
    setResetPassword('');
    setResetConfirmPassword('');
    setShowResetPassword(false);
    setEmailTouched(false);
  };

  const returnToSignIn = () => {
    clearForgotState();
    setIsSignUp(false);
    setMessage({ type: '', text: '' });
  };

  useEffect(() => {
    if (signupFromQuery) {
      setIsSignUp(true);
    }
  }, [signupFromQuery]);
  // "Forgot password?" in the sign-in pop-up lands here, on the reset step, with what was typed.
  useEffect(() => {
    if (location.state?.forgot) {
      setForgotStep('email');
      setResetEmail(location.state.email || '');
    }
  }, [location.state]);

  useEffect(() => {
    if (forgotStep) return undefined;

    setEmail('');
    setPassword('');
    setFullName('');

    const pendingInvite = sessionStorage.getItem('pendingInviteUrl');
    const pendingJoin = readPendingAction();
    const hasJoinChatPending =
      pendingJoin?.type === 'join_group_chat' && Boolean(pendingJoin.groupId);
    setHasPendingInvite(!!pendingInvite || !!safeNextPath || hasJoinChatPending);

    supabase.auth.getSession().then(({ data: { session } }) => {
      // Offline with an expired token getSession() answers null; the stored session still means signed in.
      const effectiveSession = toEffectiveAuthSession(resolveAuthSession(session));
      setSession(effectiveSession);
      if (effectiveSession?.user?.id) {
        redirectAfterAuth();
      }
    });
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      const effectiveSession = toEffectiveAuthSession(resolveAuthSession(session, { event }), {
        event,
      });
      setSession(effectiveSession);
      if (effectiveSession?.user?.id) {
        redirectAfterAuth();
      }
    });
    return () => subscription.unsubscribe();
  }, [navigate, safeNextPath, forgotStep]);

  const handleSignOut = async () => {
    clearPasswordRecoveryActive();
    clearAllQueryCache(queryClient, session?.user?.id ?? null);
    await supabase.auth.signOut();
    Object.keys(localStorage).forEach((key) => {
      if (key !== 'prii-theme') localStorage.removeItem(key);
    });
    setEmail('');
    setPassword('');
    setFullName('');
    setMessage({ type: '', text: '' });
    window.location.href = '/';
  };

  const sendResetCode = async (targetEmail) => {
    const trimmedEmail = normalizeEmail(targetEmail);
    if (!isValidEmail(trimmedEmail)) {
      setEmailTouched(true);
      setMessage({
        type: 'error',
        text: trimmedEmail
          ? 'Enter a full email, like name@example.com.'
          : 'Enter your email address.',
      });
      return false;
    }

    setMessage({ type: '', text: '' });
    const { error } = await supabase.auth.resetPasswordForEmail(trimmedEmail, {
      redirectTo: getResetPasswordRedirectUrl(),
    });
    if (error) throw error;

    setResetEmail(trimmedEmail);
    setResendIn(EMAIL_RESEND_SECONDS);
    showToast('Check your email for your reset code.');
    return true;
  };

  const handleSendResetCode = async (e) => {
    e.preventDefault();
    setSendCodeLoading(true);
    try {
      const sent = await sendResetCode(resetEmail);
      if (sent) {
        setForgotStep('code');
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: friendlyAuthError(error, 'Could not send the reset email.'),
      });
    } finally {
      setSendCodeLoading(false);
    }
  };

  const handleResendResetCode = async () => {
    if (!resetEmail.trim()) {
      setForgotStep('email');
      setMessage({ type: 'error', text: 'Enter your email address again.' });
      return;
    }

    setResendLoading(true);
    setMessage({ type: '', text: '' });
    try {
      await sendResetCode(resetEmail);
    } catch (error) {
      setMessage({ type: 'error', text: friendlyAuthError(error, 'Could not resend the code.') });
    } finally {
      setResendLoading(false);
    }
  };

  const handleResetPasswordWithCode = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    const code = resetCode.trim();
    // A 6-digit cap once cut the real 8-digit code short and the server answered "expired" (operator, 2026-09-23).
    if (code.length !== OTP_LENGTH) {
      setMessage({ type: 'error', text: `Enter the ${OTP_LENGTH}-digit code.` });
      return;
    }
    if (!isPasswordAcceptable(resetPassword)) {
      setMessage({ type: 'error', text: 'Your new password needs everything on the list.' });
      return;
    }
    if (resetPassword !== resetConfirmPassword) {
      setMessage({ type: 'error', text: "Passwords don't match." });
      return;
    }

    setResetSubmitLoading(true);
    markPasswordRecoveryActive();
    try {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email: resetEmail.trim(),
        token: code,
        type: 'recovery',
      });
      if (verifyError) {
        clearPasswordRecoveryActive();
        setMessage({ type: 'error', text: formatOtpVerificationError(verifyError) });
        return;
      }

      const { error: updateError } = await supabase.auth.updateUser({ password: resetPassword });
      if (updateError) {
        clearPasswordRecoveryActive();
        await supabase.auth.signOut();
        setMessage({
          type: 'error',
          text: friendlyAuthError(updateError, 'Could not update your password.'),
        });
        return;
      }

      // The code proved it is you and the new password is set, so you stay signed in - the app picks the session up
      // once the recovery flag is cleared (it ignores recovery sessions until then).
      clearPasswordRecoveryActive();
      clearForgotState();
      showToast("Password updated. You're signed in.");
      if (redirectAfterAuth()) return;
      navigate('/');
    } catch (error) {
      clearPasswordRecoveryActive();
      setMessage({
        type: 'error',
        text: friendlyAuthError(error, 'Could not reset your password.'),
      });
    } finally {
      setResetSubmitLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    setLoading(true);
    const result = await submitAuth({ isSignUp, email, password, name: fullName }); // lib/auth/submitAuth: the pop-up's too
    setLoading(false);
    if (result.error) {
      if (result.field) setEmailTouched(true);
      setMessage({ type: 'error', text: result.error });
      return;
    }
    setEmail('');
    setPassword('');
    setFullName('');
    if (!isSignUp) showToast('Signed in successfully!');
    if (redirectAfterAuth()) return;
    navigate('/');
  };

  if (session) {
    return (
      <AuthShell>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-dark-text text-center">
          Signed in
        </h1>
        <p className="mt-2 text-center text-gray-500 dark:text-dark-muted text-sm">
          {session.user?.email}
        </p>
        <div className="mt-6 flex flex-col gap-3">
          <Button type="button" variant="secondary" fullWidth onClick={handleSignOut}>
            Sign out
          </Button>
          <Button type="button" variant="primary" fullWidth onClick={() => navigate('/')}>
            Back to events
          </Button>
        </div>
      </AuthShell>
    );
  }

  if (forgotStep === 'email') {
    return (
      <AuthShell>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-dark-text text-center mb-2">
          Reset password
        </h1>
        <p className="mb-6 text-center text-sm text-gray-500 dark:text-dark-muted">
          Enter your email and we&apos;ll send you a reset code.
        </p>
        <form onSubmit={handleSendResetCode} noValidate className="flex flex-col gap-4">
          <EmailField
            name="reset-email"
            value={resetEmail}
            onChange={setResetEmail}
            onBlur={() => resetEmail && setEmailTouched(true)}
            showError={emailTouched}
            disabled={sendCodeLoading}
          />
          <Button type="submit" fullWidth loading={sendCodeLoading} size="lg">
            Send code
          </Button>
        </form>
        {message.type === 'error' && message.text && (
          <p className="mt-4 text-sm text-center text-danger">{message.text}</p>
        )}
        <Button
          type="button"
          variant="ghost"
          fullWidth
          onClick={returnToSignIn}
          disabled={sendCodeLoading}
          className="mt-4 !h-auto px-2 py-0 text-center text-sm font-medium !text-primary hover:!underline disabled:!opacity-60"
        >
          Back to log in
        </Button>
      </AuthShell>
    );
  }

  if (forgotStep === 'code') {
    return (
      <AuthShell>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-dark-text text-center mb-2">
          Enter reset code
        </h1>
        <p className="mb-6 text-center text-sm text-gray-500 dark:text-dark-muted">
          We sent an 8-digit code to{' '}
          <span className="font-medium text-gray-700 dark:text-dark-text">{resetEmail}</span>.
        </p>
        <form onSubmit={handleResetPasswordWithCode} noValidate className="flex flex-col gap-4">
          <TextInput
            type="text"
            name="reset-code"
            placeholder={`${OTP_LENGTH}-digit code`}
            value={resetCode}
            onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH))}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={OTP_LENGTH}
            disabled={resetSubmitLoading}
            className="text-center tracking-[0.35em]"
          />
          {/* The account's email, hidden, so iOS Keychain files the new password under the right login. */}
          <input
            type="email"
            name="username"
            autoComplete="username"
            value={resetEmail}
            readOnly
            hidden
          />
          <PasswordInput
            name="reset-password-new"
            placeholder="New password"
            value={resetPassword}
            onChange={(e) => setResetPassword(e.target.value)}
            autoComplete="new-password"
            visible={showResetPassword}
            onToggleVisible={() => setShowResetPassword((v) => !v)}
            aria-describedby="reset-password-rules"
            disabled={resetSubmitLoading}
          />
          <PasswordRules id="reset-password-rules" password={resetPassword} />
          <PasswordInput
            name="reset-password-confirm"
            placeholder="Confirm new password"
            value={resetConfirmPassword}
            onChange={(e) => setResetConfirmPassword(e.target.value)}
            autoComplete="new-password"
            visible={showResetPassword}
            showToggle={false}
            invalid={
              resetConfirmPassword.length > 0 && !resetPassword.startsWith(resetConfirmPassword)
            }
            disabled={resetSubmitLoading}
          />
          {resetConfirmPassword.length > 0 && !resetPassword.startsWith(resetConfirmPassword) && (
            <p className="auth-field-hint auth-field-hint--error">Passwords don&apos;t match.</p>
          )}
          <Button type="submit" fullWidth loading={resetSubmitLoading} size="lg">
            Update password
          </Button>
        </form>
        {message.type === 'error' && message.text && (
          <p className="mt-4 text-sm text-center text-danger">{message.text}</p>
        )}
        <Button
          type="button"
          variant="ghost"
          fullWidth
          onClick={handleResendResetCode}
          disabled={resetSubmitLoading || resendLoading || resendIn > 0}
          className="mt-4 !h-auto px-2 py-0 text-center text-sm font-medium !text-primary hover:!underline disabled:!opacity-60"
        >
          {resendLoading
            ? 'Sending…'
            : resendIn > 0
              ? `Resend code in ${resendIn}s`
              : 'Resend code'}
        </Button>
        <Button
          type="button"
          variant="ghost"
          fullWidth
          onClick={() => {
            setForgotStep('email');
            setResetCode('');
            setResetPassword('');
            setResetConfirmPassword('');
            setMessage({ type: '', text: '' });
          }}
          disabled={resetSubmitLoading || resendLoading}
          className="mt-2 !h-auto px-2 py-0 text-center text-sm font-medium !text-primary hover:!underline disabled:!opacity-60"
        >
          Use a different email
        </Button>
        <Button
          type="button"
          variant="ghost"
          fullWidth
          onClick={returnToSignIn}
          disabled={resetSubmitLoading || resendLoading}
          className="mt-2 !h-auto px-2 py-0 text-center text-sm font-medium !text-gray-500 hover:!underline dark:!text-dark-muted disabled:!opacity-60"
        >
          Back to log in
        </Button>
      </AuthShell>
    );
  }

  const switchMode = (toSignUp) => {
    setEmail('');
    setPassword('');
    setFullName('');
    setMessage({ type: '', text: '' });
    setEmailTouched(false);
    setShowPassword(false);
    setIsSignUp(toSignUp);
  };

  // The pop-up's own Log in | Sign up switch on top (Sam, 2026-10-23: every way in looks the same; this replaced the
  // "or" + Create an account button of the morning's option 2).
  const modeSwitch = (
    <div className="mb-6 grid grid-cols-2 gap-1 rounded-btn bg-white/5 p-1" role="tablist">
      {[
        ['Log in', false],
        ['Sign up', true],
      ].map(([label, su]) => (
        <Button
          key={label}
          type="button"
          role="tab"
          aria-selected={isSignUp === su}
          variant={isSignUp === su ? 'chipSelected' : 'ghost'}
          size="md"
          onClick={() => isSignUp !== su && switchMode(su)}
          disabled={loading}
        >
          {label}
        </Button>
      ))}
    </div>
  );

  return (
    <AuthShell>
      <h1 className="sr-only">{isSignUp ? 'Create account' : 'Log in'}</h1>
      {modeSwitch}
      {hasPendingInvite && (
        <div className="mb-4 p-3 bg-primary/10 border border-primary/20 rounded-lg">
          <p className="text-sm text-primary font-medium text-center">Log in to join your group!</p>
        </div>
      )}
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        {isSignUp && (
          <TextInput
            type="text"
            name="name"
            placeholder="Display name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            autoComplete="nickname"
            maxLength={40}
            disabled={loading}
          />
        )}
        <EmailField
          name="email"
          autoComplete={isSignUp ? 'email' : 'username'}
          value={email}
          onChange={setEmail}
          onBlur={() => email && setEmailTouched(true)}
          showError={emailTouched}
          disabled={loading}
        />
        <PasswordInput
          name="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          // iOS offers saved passwords for current-password and suggests a strong one for new-password.
          autoComplete={isSignUp ? 'new-password' : 'current-password'}
          visible={showPassword}
          onToggleVisible={() => setShowPassword((v) => !v)}
          aria-describedby={isSignUp ? 'signup-password-rules' : undefined}
          disabled={loading}
        />
        {isSignUp && <PasswordRules id="signup-password-rules" password={password} />}
        {!isSignUp && (
          <div className="flex justify-end -mt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setForgotStep('email');
                setResetEmail(email.trim());
                setMessage({ type: '', text: '' });
              }}
              disabled={loading}
              className="!h-auto px-1 py-0 text-sm font-medium !text-primary hover:!underline disabled:!opacity-60"
            >
              Forgot password?
            </Button>
          </div>
        )}
        <Button type="submit" fullWidth loading={loading} size="lg">
          {isSignUp ? 'Create account' : 'Log in'}
        </Button>
        {/* Agreeing is part of signing up, said where you sign up (App Store review; Sam, 2026-10-27). */}
        {isSignUp && (
          <p className="mt-3 text-center text-xs text-gray-500 dark:text-dark-muted">
            By creating an account you agree to the{' '}
            <Link to="/terms" className="underline hover:text-gray-700 dark:hover:text-dark-text">
              Terms
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="underline hover:text-gray-700 dark:hover:text-dark-text">
              Privacy Policy
            </Link>
            .
          </p>
        )}
      </form>
      {message.type === 'error' && message.text && (
        <p className="mt-4 text-sm text-center text-danger">{message.text}</p>
      )}
      <p className="mt-4 text-center">
        <Link
          to="/"
          className="text-sm text-gray-500 dark:text-dark-muted hover:text-gray-700 dark:hover:text-dark-text"
        >
          ← Back to events
        </Link>
      </p>
    </AuthShell>
  );
}
